import { useState } from 'react';
import { Typography } from 'antd';
import { CloseOutlined, LeftOutlined, RightOutlined, SwapOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import FilePreviewPanel from '../FolderPreview/FilePreviewPanel';
import type { ITreeNode } from './types';

const { Text } = Typography;

/**
 * One editor group. Its role in the compare pair (A / B / reference) is shown
 * on the tab; the file view goes through `FilePreviewPanel` with
 * `showHeader={false}` (Faza 1 decision) — no duplicated name/download/
 * fullscreen chrome, `PdfPreview`'s own toolbar stays the only toolbar.
 */

interface IEditorGroupProps {
  groupId: string;
  file: ITreeNode | null;
  /** Ancestor chain (root → … → file), resolved from the tree — see resolve.ts. */
  breadcrumb: ITreeNode[];
  index: number;
  total: number;
  role: 'a' | 'b' | null;
  modelInstanceId?: string;
  /** A file is being dragged from the Explorer. */
  isDragging: boolean;
  onToggleCompare: () => void;
  onMove: (dir: -1 | 1) => void;
  onClose: () => void;
  onDropFile: (fileId: string) => void;
}

const fileExtension = (name: string): string | undefined => {
  const i = name.lastIndexOf('.');
  return i > 0 ? name.slice(i + 1).toLowerCase() : undefined;
};

const EditorGroup: React.FC<IEditorGroupProps> = ({
  groupId,
  file,
  breadcrumb,
  index,
  total,
  role,
  modelInstanceId,
  isDragging,
  onToggleCompare,
  onMove,
  onClose,
  onDropFile,
}) => {
  const [dropping, setDropping] = useState(false);
  const inPair = role !== null;

  return (
    <Wrapper
      $inPair={inPair}
      data-gid={groupId}
      onDragOver={e => {
        if (e.dataTransfer.types.includes('text/plain')) {
          e.preventDefault();
          setDropping(true);
        }
      }}
      onDragLeave={() => setDropping(false)}
      onDrop={e => {
        e.preventDefault();
        setDropping(false);
        const id = e.dataTransfer.getData('text/plain');
        if (id) onDropFile(id);
      }}
    >
      <TabsRow>
        <Tab>
          <RoleTag $inPair={inPair}>{inPair ? role!.toUpperCase() : 'ref'}</RoleTag>
          <Name ellipsis={{ tooltip: file ? file.name : undefined }}>
            {file ? file.name : 'empty'}
          </Name>
        </Tab>
        <Actions>
          <ActBtn
            $on={inPair}
            title={inPair ? 'In the compare pair — click to remove' : 'Set as a compare side (A/B)'}
            onClick={onToggleCompare}
          >
            <SwapOutlined />
          </ActBtn>
          <ActBtn title="Move left" disabled={index === 0} onClick={() => onMove(-1)}>
            <LeftOutlined />
          </ActBtn>
          <ActBtn title="Move right" disabled={index === total - 1} onClick={() => onMove(1)}>
            <RightOutlined />
          </ActBtn>
          <ActBtn title="Close group" disabled={total <= 1} onClick={onClose}>
            <CloseOutlined />
          </ActBtn>
        </Actions>
      </TabsRow>

      {file && breadcrumb.length > 0 && (
        <Crumbs>
          {breadcrumb.map((n, i) => (
            <span key={n.id}>
              {i > 0 && <Sep>›</Sep>}
              {i === breadcrumb.length - 1 ? <b>{n.name}</b> : n.name}
            </span>
          ))}
        </Crumbs>
      )}

      <ViewArea $dropping={dropping} $dragging={isDragging}>
        {file && file.fileId ? (
          <FilePreviewPanel
            file={{
              id: file.fileId,
              name: file.name,
              path: file.filePath,
              extension: file.extension ?? fileExtension(file.name),
            }}
            modelInstanceId={file.modelInstanceId || modelInstanceId}
            showHeader={false}
          />
        ) : (
          <Empty>
            Empty group — open a file from the Explorer, or drag one here.
          </Empty>
        )}
      </ViewArea>
    </Wrapper>
  );
};

export default EditorGroup;

const Wrapper = styled.div<{ $inPair: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
  height: 100%;
  border-right: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-right: none;
  }
`;

const TabsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 4px 6px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  flex-shrink: 0;
`;

const Tab = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const RoleTag = styled.span<{ $inPair: boolean }>`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  padding: 0 5px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme, $inPair }) => ($inPair ? theme.colors.primaryActive : theme.colors.textSecondary)};
  background: ${({ theme, $inPair }) => ($inPair ? theme.colors.primaryLight : theme.colors.gray[100])};
`;

const Name = styled(Text)`
  min-width: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const ActBtn = styled.button<{ $on?: boolean }>`
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  font-size: 12px;
  color: ${({ theme, $on }) => ($on ? theme.colors.primary : theme.colors.textSecondary)};
  background: ${({ theme, $on }) => ($on ? theme.colors.primaryLighter : 'transparent')};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.cardHover};
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const Crumbs = styled.div`
  padding: 3px 8px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Sep = styled.span`
  margin: 0 4px;
  opacity: 0.6;
`;

const ViewArea = styled.div<{ $dropping: boolean; $dragging: boolean }>`
  flex: 1;
  min-height: 0;
  outline: ${({ $dropping, theme }) => ($dropping ? `2px dashed ${theme.colors.primary}` : 'none')};
  outline-offset: -2px;

  iframe {
    pointer-events: ${({ $dragging }) => ($dragging ? 'none' : 'auto')};
  }
`;

const Empty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;
